// src/pages/taxonomy/Sizes/SizesFormPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { SizesAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';
const KIND_OPTIONS = [
  { value: 'bag',     label: 'Bag' },
  { value: 'shoes',   label: 'Shoes' },
  { value: 'wallet',  label: 'Wallet' },
  { value: 'generic', label: 'Generic / Legacy' },
];

export default function SizesFormPage() {
  const { id }      = useParams();
  const history     = useHistory();
  const isEdit      = Boolean(id);

  const [name, setName]                   = useState('');
  const [code, setCode]                   = useState('');
  const [kind, setKind]                   = useState('bag');
  const [minCm, setMinCm]                 = useState('');
  const [maxCm, setMaxCm]                 = useState('');
  const [dimensionText, setDimensionText] = useState('');
  const [active, setActive]               = useState(true);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    async function load() {
      if (!isEdit) return;
      try {
        setLoading(true);
        const payload = await SizesAPI.show(id);
        const item    = payload?.item ?? payload?.data ?? payload ?? {};

        setName(item.name || '');
        setCode(item.code || '');
        setKind(item.kind || 'bag');
        setMinCm(item.min_cm ?? '');
        setMaxCm(item.max_cm ?? '');
        setDimensionText(item.dimension_text || '');
        setActive(item.active !== false); // default true
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, isEdit]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setLoading(true);

      const payload = {
        name,
        code,
        kind,
        min_cm:          minCm === '' ? null : Number(minCm),
        max_cm:          maxCm === '' ? null : Number(maxCm),
        dimension_text:  dimensionText,
        active,
      };

      if (isEdit) {
        await SizesAPI.update(id, payload);
      } else {
        await SizesAPI.create(payload);
      }

      history.push(`${BASE}/sizes`);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{isEdit ? 'Edit' : 'New'} Size</h3>
        <Link to={`${BASE}/sizes`}>&larr; Back</Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Row 1: name / code / kind */}
        <div className="form-row">
          <div className="form-group col-md-4">
            <label>Size name</label>
            <input
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Small / Medium / Large"
              required
            />
          </div>

          <div className="form-group col-md-4">
            <label>Size code</label>
            <input
              className="form-control"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="S / M / L / XL"
              required
            />
          </div>

          <div className="form-group col-md-4">
            <label>Kind</label>
            <select
              className="form-control"
              value={kind}
              onChange={e => setKind(e.target.value)}
            >
              {KIND_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <small className="text-muted">
              bag = tas, shoes = sepatu, wallet = dompet,
              generic = legacy / fallback.
            </small>
          </div>
        </div>

        {/* Row 2: cm range */}
        <div className="form-row">
          <div className="form-group col-md-3">
            <label>Min cm</label>
            <input
              className="form-control"
              type="number"
              min="0"
              value={minCm}
              onChange={e => setMinCm(e.target.value)}
              placeholder="20"
            />
          </div>

          <div className="form-group col-md-3">
            <label>Max cm</label>
            <input
              className="form-control"
              type="number"
              min="0"
              value={maxCm}
              onChange={e => setMaxCm(e.target.value)}
              placeholder="25 or blank"
            />
          </div>

          <div className="form-group col-md-3 d-flex align-items-center">
            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="activeCheck"
                checked={active}
                onChange={e => setActive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="activeCheck">
                Active
              </label>
            </div>
          </div>
        </div>

        {/* Row 3: dimension text */}
        <div className="form-group">
          <label>Definition / Notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={dimensionText}
            onChange={e => setDimensionText(e.target.value)}
            placeholder="Bag: dominant side 26–29 cm.
Shoes M: normal shoes (flat, heels, loafers, sneakers).
Wallet L: long wallet / more than one fold."
          />
        </div>

        <button className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
